import React from 'react';

import type { ConsultationFieldDefinition } from '../types/Consultation';
import { ConsultationAttachmentsFieldInput } from './ConsultationAttachmentsFieldInput';
import { ConsultationVoiceFieldInput } from './ConsultationVoiceFieldInput';
import { ConsultationRepeaterFieldInput } from './ConsultationRepeaterFieldInput';

interface ConsultationFieldInputProps {
  field: ConsultationFieldDefinition;
  consultationId?: string;
  value: string;
  onChange: (value: string) => void;
  onPendingAttachmentsChange?: (files: File[]) => void;
  onPendingVoiceChange?: (file: File | null, durationSeconds?: number) => void;
  disabled?: boolean;
}

const parseOptions = (raw?: ConsultationFieldDefinition['options']): Array<{ id: string; label: string }> => {
  if (!raw || raw.length === 0) return [];
  return raw;
};

export const ConsultationFieldInput: React.FC<ConsultationFieldInputProps> = ({
  field,
  consultationId,
  value,
  onChange,
  onPendingAttachmentsChange,
  onPendingVoiceChange,
  disabled = false,
}) => {
  const placeholder = field.placeholder ?? field.description ?? '';

  if (field.fieldType === 'repeater') {
    return (
      <ConsultationRepeaterFieldInput
        field={field}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    );
  }

  if (field.fieldType === 'voice') {
    return (
      <ConsultationVoiceFieldInput
        field={field}
        consultationId={consultationId}
        value={value}
        onChange={onChange}
        onPendingVoiceChange={onPendingVoiceChange}
        disabled={disabled}
      />
    );
  }

  if (field.fieldType === 'attachments') {
    return (
      <ConsultationAttachmentsFieldInput
        field={field}
        consultationId={consultationId}
        value={value}
        onChange={onChange}
        onPendingAttachmentsChange={onPendingAttachmentsChange}
        disabled={disabled}
      />
    );
  }

  switch (field.fieldType) {
    case 'longText':
      return (
        <textarea
          className="text-input"
          rows={4}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
        />
      );

    case 'number':
    case 'decimal':
      return (
        <input
          type="number"
          step={field.fieldType === 'decimal' ? '0.01' : '1'}
          className="text-input"
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
        />
      );

    case 'boolean':
      return (
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={value === 'true'}
            disabled={disabled}
            onChange={(event) => onChange(event.target.checked ? 'true' : 'false')}
          />
          <span>{field.description || field.name}</span>
        </label>
      );

    case 'date':
      return (
        <input
          type="date"
          className="text-input"
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        />
      );

    case 'time':
      return (
        <input
          type="time"
          className="text-input"
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        />
      );

    case 'dateTime':
      return (
        <input
          type="datetime-local"
          className="text-input"
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        />
      );

    case 'singleSelect': {
      const options = parseOptions(field.options);
      return (
        <select className="text-input" value={value} onChange={(event) => onChange(event.target.value)} disabled={disabled}>
          <option value="">-</option>
          {options.map((option) => (
            <option key={option.id} value={option.id}>{option.label}</option>
          ))}
        </select>
      );
    }

    case 'multiSelect': {
      const options = parseOptions(field.options);
      const selected = value ? value.split(',').map((item) => item.trim()).filter(Boolean) : [];
      const toggle = (optionId: string) => {
        const next = selected.includes(optionId)
          ? selected.filter((item) => item !== optionId)
          : [...selected, optionId];
        onChange(next.join(','));
      };

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {options.map((option) => (
            <label key={option.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={selected.includes(option.id)}
                disabled={disabled}
                onChange={() => toggle(option.id)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      );
    }

    default:
      return (
        <input
          type="text"
          className="text-input"
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
        />
      );
  }
};

export default ConsultationFieldInput;
